import { type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import type { Severity } from '../../lib/game';
import { Countdown } from '../primitives/Countdown';
import { AlertIcon, InfoIcon, CheckIcon, CometIcon } from '../../icons/ui-icons';

export interface AlertCardProps {
  severity?: Severity;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  /** Deadline for reacting — a comet impact, an incoming fleet, an expiring offer. */
  deadline?: number | string | Date;
  /** Primary and secondary responses. */
  actions?: ReactNode;
  /** Flashes the border — reserve for threats that can still be prevented. */
  urgent?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const SEVERITY = {
  danger: {
    frame: 'border-danger/50 bg-danger/[0.06]',
    text: 'text-danger-bright',
    icon: AlertIcon,
  },
  warning: {
    frame: 'border-warning/50 bg-warning/[0.06]',
    text: 'text-warning-bright',
    icon: AlertIcon,
  },
  success: {
    frame: 'border-success/45 bg-success/[0.06]',
    text: 'text-success-bright',
    icon: CheckIcon,
  },
  info: {
    frame: 'border-info/40 bg-info/[0.05]',
    text: 'text-info-bright',
    icon: InfoIcon,
  },
} as const;

/**
 * System event / alert card (GDD §4 wireframe, "SYSTEM-EVENTS / ALERT CARD").
 *
 * This is the component the comet-collision loop runs through: it has to carry
 * the threat, the time left to react, and the three responses (intercept,
 * shield, accept the hit) in one glance-readable block.
 *
 * `urgent` drives a flashing border and is deliberately gated behind
 * prefers-reduced-motion in the stylesheet — a player who has asked the OS to
 * calm animations still gets the colour and the countdown.
 */
export function AlertCard({
  severity = 'info',
  title,
  description,
  icon,
  deadline,
  actions,
  urgent = false,
  onDismiss,
  className,
}: AlertCardProps) {
  const config = SEVERITY[severity];
  const FallbackIcon = config.icon;

  return (
    <div
      role={severity === 'danger' ? 'alert' : 'status'}
      className={cn(
        'relative rounded-control border px-3 py-2.5',
        config.frame,
        urgent && 'motion-safe:animate-alert-flash',
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className={cn('mt-0.5 shrink-0', config.text)}>
          {icon ?? <FallbackIcon size={18} />}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span
              className={cn(
                'font-display text-xs font-semibold uppercase tracking-[0.1em]',
                config.text,
              )}
            >
              {title}
            </span>
            {deadline ? (
              <Countdown
                target={deadline}
                urgentBelow={3600}
                className="text-xs font-semibold"
                doneLabel="Jetzt"
              />
            ) : null}
          </div>

          {description ? (
            <p className="mt-1 text-xs leading-relaxed text-content-secondary">{description}</p>
          ) : null}

          {actions ? <div className="mt-2 flex flex-wrap gap-1.5">{actions}</div> : null}
        </div>

        {onDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Meldung schließen"
            className="shrink-0 rounded p-0.5 text-content-muted transition-colors hover:text-content"
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
}

export interface CometAlertProps {
  /** Impact time. */
  impactAt: number | string | Date;
  /** Response handlers — all three are the GDD's sanctioned reactions. */
  onIntercept?: () => void;
  onShield?: () => void;
  onIgnore?: () => void;
  /** Disable interception when no missile is available in the silo. */
  interceptDisabled?: boolean;
  className?: string;
}

/**
 * Pre-composed comet-collision alert.
 *
 * Encodes the exact decision from GDD §3: intercept (missile from the silo),
 * evade (raise the shield dome), or take the hit for a 24 h silicate-rich
 * crater. Shipped as its own component because this alert appears every few
 * hours per colony and must be identical every time.
 */
export function CometAlert({
  impactAt,
  onIntercept,
  onShield,
  onIgnore,
  interceptDisabled = false,
  className,
}: CometAlertProps) {
  return (
    <AlertCard
      severity="danger"
      urgent
      icon={<CometIcon size={18} />}
      title="Warnung: Kometen-Einschlag"
      deadline={impactAt}
      description="Ein Komet befindet sich auf Kollisionskurs. Ohne Reaktion werden 1–2 Gebäude beschädigt; der Krater hinterlässt 24 h lang eine hohe Silikat-Konzentration."
      className={className}
      actions={
        <>
          {onIntercept ? (
            <AlertAction onClick={onIntercept} disabled={interceptDisabled} tone="danger">
              Abfangen
            </AlertAction>
          ) : null}
          {onShield ? (
            <AlertAction onClick={onShield} tone="neutral">
              Schildkuppel
            </AlertAction>
          ) : null}
          {onIgnore ? (
            <AlertAction onClick={onIgnore} tone="ghost">
              Einschlag zulassen
            </AlertAction>
          ) : null}
        </>
      }
    />
  );
}

function AlertAction({
  children,
  onClick,
  disabled,
  tone,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone: 'danger' | 'neutral' | 'ghost';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-7 items-center rounded-control border px-2.5',
        'font-display text-label uppercase tracking-[0.1em] transition-all',
        'duration-[var(--sg-duration-fast)] disabled:pointer-events-none disabled:opacity-40',
        tone === 'danger' &&
          'border-danger/50 bg-danger/15 text-danger-bright hover:bg-danger/25 hover:shadow-glow-danger',
        tone === 'neutral' &&
          'border-edge bg-surface-3 text-content-secondary hover:bg-surface-raised hover:text-content',
        tone === 'ghost' &&
          'border-transparent text-content-secondary hover:bg-surface-3 hover:text-content',
      )}
    >
      {children}
    </button>
  );
}
